/**
 * Client for the flattened, parameter-free `news-feed/trends` Songbird
 * endpoint — bundles all 3 market-trend tabs (most_active/top_gainers/
 * top_losers) and all 6 sector tabs in one request, since every visitor to
 * the /news/ page gets the exact same data for those tabs.
 *
 * The original parameterized `stocks/news/filtered` endpoint is untouched;
 * callers of this module should fall back to it (see
 * `utils/songbird-client.ts` consumers in MarketsTrends.vue /
 * MarketsTrendingTopics.vue) if this bundled fetch fails or a specific
 * tab/sector comes back empty.
 */

import axios from "axios";
import { getSongbirdApiBase } from "./songbird-client";

/**
 * @param {object} [options]
 * @param {ReturnType<typeof useRuntimeConfig>} [options.config]
 * @param {number} [options.timeout]
 * @returns {Promise<{ trends: Record<string, any[]>, sectors: Record<string, any[]> } | null>}
 */
export async function fetchNewsFeedTrends({ config, timeout } = {}) {
	const resolvedConfig = config || useRuntimeConfig();
	const songbirdBase = getSongbirdApiBase(resolvedConfig);
	const current_hostname = resolvedConfig.public.current_hostname;

	const response = await axios.get(`${songbirdBase}news-feed/trends`, {
		headers: { "X-Original-Origin": current_hostname },
		timeout: timeout ?? (import.meta.server ? 8000 : 15000)
	});

	const data = response?.data;
	if (!data || typeof data !== "object") return null;

	return {
		trends: data.trends || {},
		sectors: data.sectors || {}
	};
}

/**
 * Client for the flattened, parameter-free `news-feed/top-gainers` Songbird
 * endpoint — bundles the top-20-gainers list and their 1D sparklines in one
 * request, since every caller (TopGainerStocks.vue / QuoteFinder.vue, via
 * useStocksStore.fetchTopGainerStocks) fetches the exact same two-step data
 * with hardcoded params (value=top_gainers, limit=20, range=1D).
 *
 * The original parameterized `stocks/filtered` + `stocks/history` endpoints
 * are untouched; useStocksStore falls back to them if this bundled fetch
 * fails or comes back empty.
 *
 * @param {object} [options]
 * @param {ReturnType<typeof useRuntimeConfig>} [options.config]
 * @param {number} [options.timeout]
 * @returns {Promise<{ quotes: Record<string, any[]>, sparklines: Record<string, any[]> } | null>}
 */
export async function fetchNewsFeedTopGainers({ config, timeout } = {}) {
	const resolvedConfig = config || useRuntimeConfig();
	const songbirdBase = getSongbirdApiBase(resolvedConfig);
	const current_hostname = resolvedConfig.public.current_hostname;

	const response = await axios.get(`${songbirdBase}news-feed/top-gainers`, {
		headers: { "X-Original-Origin": current_hostname },
		timeout: timeout ?? (import.meta.server ? 8000 : 15000)
	});

	const data = response?.data;
	if (!data || typeof data !== "object") return null;

	return {
		quotes: data.quotes || {},
		sparklines: data.sparklines || {}
	};
}

/**
 * Client for the flattened, pagination-free `news-feed/latest-news` Songbird
 * endpoint — same underlying data as `stocks/news` list mode, but strips the
 * pagination envelope (page/limit/totalRecords/totalPages/hasMore/
 * nextCursor/source) that the /news page never reads (it only ever renders
 * this fixed first page; deeper browsing is a separate WordPress-backed feed
 * on /news/page/:number).
 *
 * The original `stocks/news` endpoint is untouched; callers of this module
 * should fall back to it (see `utils/stocks-news.js`) if this fetch fails.
 *
 * @param {object} [options]
 * @param {ReturnType<typeof useRuntimeConfig>} [options.config]
 * @param {number} [options.timeout]
 * @returns {Promise<{ items: any[] } | null>}
 */
export async function fetchNewsFeedLatestNews({ config, timeout } = {}) {
	const resolvedConfig = config || useRuntimeConfig();
	const songbirdBase = getSongbirdApiBase(resolvedConfig);
	const current_hostname = resolvedConfig.public.current_hostname;

	const response = await axios.get(`${songbirdBase}news-feed/latest-news`, {
		headers: { "X-Original-Origin": current_hostname },
		timeout: timeout ?? (import.meta.server ? 8000 : 15000)
	});

	const data = response?.data;
	if (!data || typeof data !== "object") return null;

	return { items: Array.isArray(data.items) ? data.items : [] };
}

/** Map of MarketsTrends.vue tab id -> news-feed/trends `trends` key. */
export const TRENDS_TAB_FILTER_MAP = {
	most_active: "most_active",
	gainers: "top_gainers",
	losers: "top_losers"
};

/** Map of MarketsTrendingTopics.vue tab label -> news-feed/trends `sectors` key. */
export const TRENDING_TOPICS_SECTOR_MAP = {
	"AI & Tech": "Technology",
	"Real Estate": "Real Estate",
	Energy: "Energy",
	Healthcare: "Healthcare",
	"Consumer Goods": "Consumer Staples",
	Industrials: "Industrials"
};
